import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { forgotPassword } from '../services/api';
import theme from '../styles/theme';

const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(145deg, ${theme.colors.sidebarFrom} 0%, ${theme.colors.sidebarTo} 45%, #243044 100%);
  position: fixed;
  inset: 0;
  padding: 72px 20px 24px;
  box-sizing: border-box;
  overflow-y: auto;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 20% 20%, rgba(255, 107, 107, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse 50% 40% at 80% 80%, rgba(255, 142, 83, 0.14) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const HomeLink = styled(Link)`
  position: fixed;
  top: 20px;
  left: 20px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: ${theme.radius.pill};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  transition: all ${theme.transitions.base};
  font-size: ${theme.typography.body};
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
  }
`;

const ForgotPasswordCard = styled.div`
  background: ${theme.colors.card};
  padding: 36px 32px;
  border-radius: ${theme.radius.xl};
  box-shadow: ${theme.shadows.xl}, 0 0 0 1px rgba(255, 255, 255, 0.08);
  width: 100%;
  max-width: 420px;
  margin: auto;
  position: relative;
  z-index: 1;
  animation: slideUp 0.45s cubic-bezier(0.34, 1.2, 0.64, 1);

  @media (min-width: 1024px) {
    max-width: 460px;
    padding: 44px 40px;
  }
`;

const IconBadge = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${theme.radius.lg};
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: ${theme.colors.primary};
  font-size: 22px;
`;

const Title = styled.h1`
  font-size: ${theme.typography.h1};
  font-weight: 800;
  margin: 0 0 8px;
  text-align: center;
  background: ${theme.colors.accentGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  letter-spacing: -0.04em;
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  margin: 0 0 28px;
  font-size: ${theme.typography.body};
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Label = styled.label`
  font-size: ${theme.typography.small};
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RequiredAsterisk = styled.span`
  color: ${theme.colors.error};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1.5px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  font-size: ${theme.typography.body};
  transition: all ${theme.transitions.base};
  background: ${theme.colors.card};
  box-sizing: border-box;
  height: 44px;
  color: ${theme.colors.text};

  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.12);
  }

  &.error {
    border-color: ${theme.colors.error};
    box-shadow: 0 0 0 4px ${theme.colors.errorBg};
  }
`;

const ErrorText = styled.span`
  color: ${theme.colors.error};
  font-size: ${theme.typography.small};
  margin-top: 4px;
`;

const SubmitButton = styled.button`
  background: ${theme.colors.accentGradient};
  color: white;
  padding: 10px;
  border-radius: ${theme.radius.md};
  font-weight: 700;
  font-size: ${theme.typography.body};
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  width: 100%;
  margin-top: 10px;
  height: 48px;
  box-shadow: ${theme.shadows.glowSoft};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glow};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackToLogin = styled(Link)`
  color: ${theme.colors.primary};
  text-align: center;
  display: block;
  margin-top: 20px;
  text-decoration: none;
  font-weight: 600;
  font-size: ${theme.typography.body};
  padding: 8px;
  border-radius: ${theme.radius.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.secondary};
    background: rgba(255, 107, 107, 0.08);
  }
`;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Oops! You forgot to add your email.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const response = await forgotPassword({ email });
      toast.success(response.message || 'Password reset instructions sent to your email');
      navigate('/login');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process request';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ForgotPasswordContainer>
      <HomeLink to="/">
        <FontAwesomeIcon icon={faHome} /> Back to Home
      </HomeLink>
      <ForgotPasswordCard>
        <IconBadge>
          <FontAwesomeIcon icon={faEnvelope} />
        </IconBadge>
        <Title>Forgot Password</Title>
        <Subtitle>Enter your email and we&apos;ll send you a reset link</Subtitle>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              Email<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <InputIcon>
                <FontAwesomeIcon icon={faEnvelope} />
              </InputIcon>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={error ? 'error' : ''}
                placeholder="Enter your email"
              />
            </InputWrapper>
            {error && <ErrorText>{error}</ErrorText>}
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </SubmitButton>
        </Form>

        <BackToLogin to="/login">Remember your password? Back to Login</BackToLogin>
      </ForgotPasswordCard>
    </ForgotPasswordContainer>
  );
};

export default ForgotPassword;
